<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg shadow-lg h-auto flex flex-col items-center justify-center w-full p-6 relative"
  >
    <template v-if="showCropper && imageUrl">
      <CropperComponent
        :imageUrl="imageUrl"
        :aspect-ratio="1"
        :view-mode="1"
        @cropped="handleCroppedImage"
        @cancel="handleCancel"
        @close-dialog="handleCloseDialog"
      />
    </template>
    <template v-else>
      <div class="flex justify-between items-center mb-8 w-full">
        <h3 class="text-xl font-bold dark:text-gray-200">
          Update Profile Picture
        </h3>
        <button
          @click="$emit('close')"
          class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="flex flex-col items-center gap-6 w-full">
        <div
          class="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden relative flex items-center justify-center"
        >
          <div
            v-if="!bioImage && !currentImage"
            class="absolute inset-0 flex items-center justify-center text-gray-400"
          >
            <i class="fas fa-user text-4xl"></i>
          </div>
          <img
            v-else
            :src="getFileUrl(bioImage) || getFileUrl(currentImage)"
            class="w-full h-full object-cover"
            alt="Profile Preview"
          />
        </div>

        <div class="w-full">
          <label class="block mb-2 text-sm font-medium dark:text-gray-300"
            >Upload new photo</label
          >
          <input
            type="file"
            accept="image/*"
            @change="handleImageUpload"
            class="hidden"
            ref="fileInput"
          />
          <button
            @click="$refs.fileInput.click()"
            class="w-full bg-main-100 dark:bg-blue-gray-600 dark:hover:bg-blue-gray-900 hover:bg-main-200 text-main-800 dark:text-sub-900 py-2 px-4 rounded-lg border border-dashed border-main-300 transition"
          >
            <i class="fas fa-upload mr-2"></i> Choose Image
          </button>
        </div>

        <div class="flex gap-3 w-full" v-if="!showCropper">
          <button
            @click="$emit('close')"
            class="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded-lg transition dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            @click="saveImage"
            :disabled="!bioImage || loading"
            class="flex-1 bg-main-500 dark:bg-sub-700 hover:bg-main-600 dark:hover:bg-sub-900 text-white py-2 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="loading">
              <i class="fas fa-spinner fa-spin mr-2"></i> Saving...
            </span>
            <span v-else> Save Changes </span>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CropperComponent from "@/components/cropperComponent.vue";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
export default {
  components: { CropperComponent },
  inject: ["getFileUrl"],
  props: {
    currentImage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      imageUrl: null,
      file: null,
      showCropper: false,
      maxFileSize: 5,
      exceedFileSize: false,
      invalidFileType: false,
      aspectRatio: 1,
      bioImage: null,
      loading: false,
    };
  },
  methods: {
    handleCroppedImage(croppedCanvas) {
      const fileDetails = this.file;
      croppedCanvas.toBlob((blob) => {
        const croppedFile = new File([blob], fileDetails.name, {
          type: fileDetails.type,
        });

        this.bioImage = croppedFile;
        this.file = null;
        this.showCropper = false;
      }, fileDetails.type);
    },
    handleCancel() {
      this.imageUrl = "";
      this.file = null;
      this.showCropper = false;
    },
    handleCloseDialog() {
      const fileDetails = this.file;

      if (fileDetails) {
        this.bioImage = fileDetails;
        this.file = null;
        this.imageUrl = "";
      }
      this.showCropper = false;
    },
    handleImageUpload(event) {
      this.showCropper = false;
      const files = event.target.files;
      this.exceedFileSize = false;
      this.invalidFileType = false;

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidFileType = true;
          return;
        }

        const filesizeMb = file.size / (1024 * 1024);
        if (filesizeMb > this.maxFileSize) {
          this.exceedFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.file = file;
          this.aspectRatio = 1;
          this.showCropper = true;
          event.target.value = "";
        };
        reader.readAsDataURL(file);
      }
    },
    async saveImage() {
      if (!this.bioImage) return;
      this.loading = true;
      const formData = new FormData();
      formData.append("image", this.bioImage);

      const result = await this.$store.dispatch("updateProfileImage", formData);
      this.loading = false;
      if (result && result.success) {
        toast.success("Profile image updated successfully!");
        this.$emit("close");
      } else {
        toast.error(result?.error || "Failed to update profile image.");
      }
    },
  },
};
</script>
